import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

import { HOME_SPORT_GROUPS } from '../../pages/home/constants/home-sport-groups.constant'
import { BetConfigEntry } from '../types/bet.type'

type BetConfig = Record<string, unknown>
type MarketType = 'autoBm' | 'bookmaker' | 'fancy'

@Injectable({
	providedIn: 'root',
})
export class BetConfigService {
	betConfig$ = new BehaviorSubject<BetConfig | null>(this.loadFromStorage())

	resolveConfig(
		betConfig: BetConfig | null,
		sportId: number | string | undefined,
		marketType?: MarketType,
		eventId?: number | string,
	): Partial<BetConfigEntry> | null {
		if (!betConfig || !sportId) return null
		const mappingKey = this.getMappingKey(sportId, marketType)
		const sportConfig = this.getConfigEntry(betConfig[mappingKey]) ?? this.getConfigEntry(betConfig[String(sportId)])
		const eventConfig = this.resolveEventConfig(betConfig, sportId, mappingKey, marketType, eventId)

		if (!eventConfig) return sportConfig

		return {
			min_bet_size: eventConfig.min_bet_size ?? sportConfig?.min_bet_size,
			max_bet_size: eventConfig.max_bet_size ?? sportConfig?.max_bet_size,
		}
	}

	getMappingKey(sportId: number | string | undefined, marketType?: MarketType): string {
		if (!sportId) return ''
		const id = Number(sportId)
		const isCricket = id === HOME_SPORT_GROUPS.CRICKET
		const isSoccer = id === HOME_SPORT_GROUPS.SOCCER
		const isTennis = id === HOME_SPORT_GROUPS.TENNIS

		if (marketType === 'autoBm') {
			if (isCricket) return 'Cricket-BF-BM'
			if (isSoccer) return 'Soccer-BF-BM'
			if (isTennis) return 'Tennis-BF-BM'
		} else if (marketType === 'bookmaker') {
			if (isCricket) return 'Cricket-Bookmaker'
		} else if (marketType === 'fancy') {
			if (isCricket) return 'Cricket-Fancy'
		}

		return String(sportId)
	}

	updateBetConfig(data: unknown) {
		let parsed = data

		if (typeof data === 'string') {
			try {
				parsed = JSON.parse(data)
			} catch {
				return
			}
		}

		const config = parsed as BetConfig
		sessionStorage.setItem('betConfig', JSON.stringify(config))
		this.betConfig$.next(config)
	}

	clearBetConfig() {
		sessionStorage.removeItem('betConfig')
		this.betConfig$.next(null)
	}

	private loadFromStorage(): BetConfig | null {
		const raw = sessionStorage.getItem('betConfig')
		if (!raw) return null
		try {
			return JSON.parse(raw)
		} catch {
			return null
		}
	}

	private resolveEventConfig(
		betConfig: BetConfig,
		sportId: number | string,
		mappingKey: string,
		marketType?: MarketType,
		eventId?: number | string,
	): Partial<BetConfigEntry> | null {
		const eventConfig = this.getEventConfig(betConfig, eventId)
		if (!eventConfig) return null

		if (marketType) {
			return this.getConfigEntry(eventConfig[mappingKey]) ?? this.getConfigEntry(eventConfig[String(sportId)])
		}

		return this.getConfigEntry(eventConfig[String(sportId)]) ?? this.getConfigEntry(eventConfig['exchange'])
	}

	private getEventConfig(betConfig: BetConfig, eventId?: number | string): Record<string, unknown> | null {
		if (!eventId) return null
		const events = betConfig['events']
		if (!events || typeof events !== 'object') return null

		const eventConfig = (events as Record<string, unknown>)[String(eventId)]
		return eventConfig && typeof eventConfig === 'object' ? (eventConfig as Record<string, unknown>) : null
	}

	private getConfigEntry(value: unknown): Partial<BetConfigEntry> | null {
		if (!value || typeof value !== 'object') return null
		const entry = value as Partial<BetConfigEntry>
		return entry.min_bet_size !== undefined || entry.max_bet_size !== undefined ? entry : null
	}
}
